/**
 * Consolidation Dashboard
 * View consolidated financials across org hierarchy
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ConsolidationDashboard = () => {
  const [orgs, setOrgs] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [consolidatedTargets, setConsolidatedTargets] = useState(null);
  const [consolidatedActuals, setConsolidatedActuals] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      const token = localStorage.getItem('token');
      const bankResponse = await axios.get(`${API_URL}/api/tenants?tenant_type=bank`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (bankResponse.data.length > 0) {
        const orgResponse = await axios.get(
          `${API_URL}/api/organizations?tenant_id=${bankResponse.data[0].id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setOrgs(orgResponse.data.filter(o => o.type === 'bank_hq'));
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const consolidate = async (type) => {
    if (!selectedOrg) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const year = new Date().getFullYear();
      const date = `${year}-01-01`;
      
      const response = await axios.get(
        `${API_URL}/api/consolidation/${type}/${selectedOrg}/${year}/${date}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (type === 'targets') {
        setConsolidatedTargets(response.data);
      } else {
        setConsolidatedActuals(response.data);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error consolidating: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Consolidation Dashboard</h1>
        <p className="text-gray-600 mt-1">Roll up branch results to HQ level</p>
      </div>

      {/* Organization Selector */}
      <div className="mb-6 bg-white rounded-lg shadow p-6">
        <label className="block text-sm font-medium mb-2">Select HQ Organization</label>
        <select
          value={selectedOrg}
          onChange={(e) => setSelectedOrg(e.target.value)}
          className="w-full border rounded-lg px-4 py-2 mb-4"
        >
          <option value="">Select HQ...</option>
          {orgs.map(org => (
            <option key={org.id} value={org.id}>{org.name}</option>
          ))}
        </select>
        
        <div className="flex gap-2">
          <button
            onClick={() => consolidate('targets')}
            disabled={!selectedOrg || loading}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:bg-gray-300"
          >
            {loading ? 'Consolidating...' : 'Consolidate Targets'}
          </button>
          <button
            onClick={() => consolidate('actuals')}
            disabled={!selectedOrg || loading}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:bg-gray-300"
          >
            {loading ? 'Consolidating...' : 'Consolidate Actuals'}
          </button>
        </div>
      </div>

      {/* Results Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Targets */}
        {consolidatedTargets && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold mb-4">📊 Consolidated Targets</h2>
            <div className="text-sm text-gray-600 mb-4">
              Organization: {consolidatedTargets.organization_name}
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-green-50 rounded">
                <span className="font-medium">Revenue:</span>
                <span className="text-xl font-bold text-green-600">
                  ETB {(consolidatedTargets.revenue / 1000000).toFixed(2)}M
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-orange-50 rounded">
                <span className="font-medium">Expense:</span>
                <span className="text-xl font-bold text-orange-600">
                  ETB {(consolidatedTargets.expense / 1000000).toFixed(2)}M
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-blue-50 rounded">
                <span className="font-medium">Profit:</span>
                <span className="text-xl font-bold text-blue-600">
                  ETB {(consolidatedTargets.profit / 1000000).toFixed(2)}M
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Actuals */}
        {consolidatedActuals && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold mb-4">📈 Consolidated Actuals</h2>
            <div className="text-sm text-gray-600 mb-4">
              Organization: {consolidatedActuals.organization_name}
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-green-50 rounded">
                <span className="font-medium">Revenue:</span>
                <span className="text-xl font-bold text-green-600">
                  ETB {(consolidatedActuals.revenue / 1000000).toFixed(2)}M
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-orange-50 rounded">
                <span className="font-medium">Expense:</span>
                <span className="text-xl font-bold text-orange-600">
                  ETB {(consolidatedActuals.expense / 1000000).toFixed(2)}M
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-blue-50 rounded">
                <span className="font-medium">Profit:</span>
                <span className="text-xl font-bold text-blue-600">
                  ETB {(consolidatedActuals.profit / 1000000).toFixed(2)}M
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {!consolidatedTargets && !consolidatedActuals && (
        <div className="text-center py-12 text-gray-500">
          Select an organization and click consolidate to see results.
        </div>
      )}
    </div>
  );
};

export default ConsolidationDashboard;